import Vue from 'vue';
import _ from 'lodash';
import { lsApiService, lsErrorService } from 'ls-vue-common';
import {
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  SCHEDULES__MUTATE_SCHEDULES,
  SCHEDULES__MUTATE_SCHEDULE,
  SCHEDULES__MUTATE_LOADING_STATUS,
  SCHEDULES__GET_SCHEDULE,
  SCHEDULES__REJECT_QUOTE,
  SCHEDULES__APPROVE_QUOTE,
  SCHEDULES__SUBMIT_QUOTE_ISSUE,
  SCHEDULES__GROUPED_BY_PROPERTY,
  SCHEDULES__RESET,
  SCHEDULES__RESUME_EARLY,
  SCHEDULES__ACTIVE_SERVICES,
} from '@/store/types';
import { lsScheduleHelpers } from '@/helpers';
import { lsModuleHelpers } from '../helpers';

const createInitialState = () => {
  return {
    schedulesById: {},
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
  };
};

export const mutations = {
  [SCHEDULES__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },
  [SCHEDULES__MUTATE_SCHEDULES](state, payload) {
    state.schedulesById = _.reduce(
      payload,
      (result, schedule) => {
        const { schedule_id } = schedule;
        const existingSchedule = _.get(state.schedulesById, schedule_id, {});
        // eslint-disable-next-line no-param-reassign
        result[schedule_id] = { ...existingSchedule, ...schedule };
        return result;
      },
      {}
    );
  },

  [SCHEDULES__MUTATE_SCHEDULE](state, payload) {
    const { schedule_id } = payload;
    Vue.set(state.schedulesById, schedule_id, {
      ...state.schedulesById[schedule_id],
      ...payload,
    });
  },

  [SCHEDULES__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },
};

export const actions = {
  [SCHEDULES__GET_SCHEDULE](
    { commit, state, rootState },
    { property_id, schedule_id, include = [], forceRefresh = false } = {}
  ) {
    let requestPromise = Promise.resolve();
    const { customer_id } = rootState.auth.user;
    const { schedulesById } = state;
    const scheduleLoadedWithScheduleEvents = Boolean(
      _.get(schedulesById, `[${schedule_id}].scheduleevents`)
    );

    if (forceRefresh || !scheduleLoadedWithScheduleEvents) {
      commit(SCHEDULES__MUTATE_LOADING_STATUS, {
        isLoading: true,
      });
      requestPromise = lsApiService.request({
        method: 'GET',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/${schedule_id}`,
        params: {
          include: ['scheduleevents.contractor.user', 'scheduleevents.reviews', ...include],
        },
      });

      return requestPromise
        .then(({ data }) => {
          commit(SCHEDULES__MUTATE_SCHEDULE, data);
        })
        .catch((error) => {
          lsErrorService.log({ error, showAlert: true });
        })
        .finally(() => {
          commit(SCHEDULES__MUTATE_LOADING_STATUS, { isLoading: false });
        });
    }
    return requestPromise;
  },

  [SCHEDULES__REJECT_QUOTE](
    { commit, state, rootState },
    { schedule_id, details, onSuccess = _.noop }
  ) {
    commit(SCHEDULES__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });
    const { customer_id } = rootState.auth.user;
    const { manualquote, property_id } = state.schedulesById[schedule_id];

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/manualquotes/${
          manualquote.manualquote_id
        }/reject`,
        payload: {
          details,
        },
      })
      .then(({ data }) => {
        commit(SCHEDULES__MUTATE_SCHEDULE, {
          schedule_id,
          manualquote: {
            ...data,
          },
        });
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(SCHEDULES__MUTATE_LOADING_STATUS, { isUpdating: false });
      });
  },

  [SCHEDULES__APPROVE_QUOTE](
    { commit, dispatch, state, rootState },
    { schedule_id, backup = false }
  ) {
    const { customer_id } = rootState.auth.user;
    const { manualquote, property_id } = state.schedulesById[schedule_id];

    commit(SCHEDULES__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/manualquotes/${
          manualquote.manualquote_id
        }/authorize${backup ? '-backup' : ''}`,
      })
      .then(({ data }) => {
        commit(SCHEDULES__MUTATE_SCHEDULE, {
          schedule_id,
          manualquote: {
            ...data,
          },
        });

        // When a quote is approved it does not return the created schedule just the quoted_schedule_id
        // for now it is easiest to just dispatch to update properties and schedules to ensure the new schedule
        // displays on my services
        dispatch(PROPERTIES__GET_PROPERTIES_AND_SCHEDULES, { forceRefresh: true });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(SCHEDULES__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [SCHEDULES__SUBMIT_QUOTE_ISSUE]({ state, rootState }, { schedule_id, issues = {} }) {
    const { customer_id } = rootState.auth.user;
    const { manualquote, property_id } = state.schedulesById[schedule_id];

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/manualquotes/${
          manualquote.manualquote_id
        }/issues`,
        payload: issues,
      })
      .catch((error) => {
        lsErrorService.log({ error });
      });
  },

  [SCHEDULES__RESUME_EARLY]({ commit, dispatch, state, rootState }, { schedule_id, schedule_at }) {
    const { customer_id } = rootState.auth.user;
    const { property_id } = state.schedulesById[schedule_id];

    commit(SCHEDULES__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const requestObject = {
      method: 'POST',
      route: `/customers/${customer_id}/properties/${property_id}/schedules/${schedule_id}/unpause`,
      payload: {
        schedule_at,
      },
      apiVersion: 'v2',
    };

    return lsApiService
      .request(requestObject)
      .then(({ data }) => {
        const schedule = _.get(data, 'schedule') ? data.schedule : data;

        commit(SCHEDULES__MUTATE_SCHEDULE, {
          schedule_id,
          ...schedule,
        });

        dispatch(PROPERTIES__GET_PROPERTIES_AND_SCHEDULES, { forceRefresh: true });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(SCHEDULES__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },
};

export const getters = {
  [SCHEDULES__GROUPED_BY_PROPERTY](state) {
    return _.groupBy(state.schedulesById, 'property_id');
  },
  [SCHEDULES__ACTIVE_SERVICES](state) {
    return _.mapValues(
      _.groupBy(
        _.map(
          _.filter(state.schedulesById, (schedule) =>
            lsScheduleHelpers.isActiveServiceSchedule({ schedule })
          ),
          (schedule) => lsScheduleHelpers.getServiceFromSchedule(schedule)
        ),
        'service_id'
      ),
      (services) => services[0]
    );
  },
};

export const schedules = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
